import React, { useState, useEffect } from 'react';
import { Trash2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AdminDeleteRequestFilters } from '../components/AdminDeleteRequestFilters';
import { AdminDeleteRequestTable } from '../components/AdminDeleteRequestTable';
import { AdminDeleteRequestDetailsModal } from '../components/AdminDeleteRequestDetailsModal';
import { ApproveDeleteRequestModal } from '../components/ApproveDeleteRequestModal';
import { RejectDeleteRequestModal } from '../components/RejectDeleteRequestModal';
import { ReactivateAccountModal } from '../components/ReactivateAccountModal';
import { BulkActionsToolbar } from '../components/BulkActionsToolbar';
import { BulkApproveDeleteRequestModal } from '../components/BulkApproveDeleteRequestModal';
import { BulkRejectDeleteRequestModal } from '../components/BulkRejectDeleteRequestModal';
import {
  getDeleteRequestsDashboard,
  approveDeleteRequest,
  rejectDeleteRequest,
  searchDeleteRequests,
  bulkApproveDeleteRequests,
  bulkRejectDeleteRequests,
  reactivateAccount,
} from '../services/api';
import { DeleteRequest, DeleteRequestStatus, DeleteRequestType } from '../types';
import { ROUTES } from '../constants';

export const AdminDeleteRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<DeleteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [statusFilter, setStatusFilter] = useState<DeleteRequestStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<DeleteRequestType | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    approvedThisMonth: 0,
    rejectedThisMonth: 0,
  });

  const [selectedRequest, setSelectedRequest] = useState<DeleteRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [requestToAction, setRequestToAction] = useState<DeleteRequest | null>(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [requestToReactivate, setRequestToReactivate] = useState<DeleteRequest | null>(null);
  const [reactivationSuccess, setReactivationSuccess] = useState(false);

  const [selectedRequestIds, setSelectedRequestIds] = useState<number[]>([]);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (searchQuery !== debouncedSearchQuery) {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedRequestIds([]);
  }, [currentPage, itemsPerPage, statusFilter, typeFilter, dateFrom, dateTo, debouncedSearchQuery]);

  useEffect(() => {
    fetchRequests();
  }, [currentPage, itemsPerPage, statusFilter, typeFilter, dateFrom, dateTo, debouncedSearchQuery]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');

      if (debouncedSearchQuery.trim()) {
        const response = await searchDeleteRequests({
          name: debouncedSearchQuery,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          type: typeFilter !== 'all' ? typeFilter : undefined,
          page: currentPage,
          limit: itemsPerPage,
        });

        if (response.success) {
          setRequests(response.requests);
          setTotalPages(response.totalPages);
          setTotalRecords(response.total);
        } else {
          setError(response.error || 'Failed to search delete requests');
        }
      } else {
        const response = await getDeleteRequestsDashboard({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          type: typeFilter !== 'all' ? typeFilter : undefined,
          startDate: dateFrom || undefined,
          endDate: dateTo || undefined,
          page: currentPage,
          limit: itemsPerPage,
        });

        if (response.success) {
          setRequests(response.requests);
          setTotalPages(response.totalPages);
          setTotalRecords(response.total);

          if (response.stats) {
            setStats({
              totalRequests: response.stats.totalRequests,
              pendingRequests: response.stats.pendingRequests,
              approvedThisMonth: response.stats.approvedThisMonth,
              rejectedThisMonth: response.stats.rejectedThisMonth,
            });
          }
        } else {
          setError(response.error || 'Failed to fetch delete requests');
        }
      }
    } catch (err) {
      setError('An error occurred while fetching delete requests');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleViewDetails = (requestId: number) => {
    const request = requests.find((r) => r.requestId === requestId);
    if (request) {
      setSelectedRequest(request);
      setShowDetailsModal(true);
    }
  };

  const handleApproveClick = (requestId: number) => {
    const request = requests.find((r) => r.requestId === requestId);
    if (request) {
      setRequestToAction(request);
      setShowApproveModal(true);
      setShowDetailsModal(false);
    }
  };

  const handleRejectClick = (requestId: number) => {
    const request = requests.find((r) => r.requestId === requestId);
    if (request) {
      setRequestToAction(request);
      setShowRejectModal(true);
      setShowDetailsModal(false);
    }
  };

  const handleApproveConfirm = async () => {
    if (!requestToAction) return;

    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      const response = await approveDeleteRequest({ requestId: requestToAction.requestId });

      if (response.success) {
        setSuccess('Delete request approved successfully. Account has been permanently deleted.');
        setShowApproveModal(false);
        setRequestToAction(null);
        await fetchRequests();
      } else {
        setError(response.error || 'Failed to approve delete request');
      }
    } catch (err) {
      setError('An error occurred while approving the request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!requestToAction) return;

    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      const response = await rejectDeleteRequest({
        requestId: requestToAction.requestId,
        reason,
      });

      if (response.success) {
        setSuccess('Delete request rejected successfully. User has been notified.');
        setShowRejectModal(false);
        setRequestToAction(null);
        await fetchRequests();
      } else {
        setError(response.error || 'Failed to reject delete request');
      }
    } catch (err) {
      setError('An error occurred while rejecting the request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateClick = (requestId: number) => {
    const request = requests.find((r) => r.requestId === requestId);
    if (request) {
      setRequestToReactivate(request);
      setShowReactivateModal(true);
      setShowDetailsModal(false);
    }
  };

  const handleReactivateConfirm = async () => {
    if (!requestToReactivate) return;

    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      const reactivatePayload = requestToReactivate.accountType === 'provider'
        ? { providerId: requestToReactivate.providerId }
        : { userId: requestToReactivate.userId };

      const response = await reactivateAccount(reactivatePayload);

      if (response.success) {
        setReactivationSuccess(true);
        await fetchRequests();
      } else {
        setError(response.error || 'Failed to reactivate account');
      }
    } catch (err) {
      setError('An error occurred while reactivating the account');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleSelectionChange = (selectedIds: number[]) => {
    setSelectedRequestIds(selectedIds);
  };

  const handleBulkApproveClick = () => {
    setShowBulkApproveModal(true);
  };

  const handleBulkRejectClick = () => {
    setShowBulkRejectModal(true);
  };

  const handleBulkApproveConfirm = async () => {
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      const response = await bulkApproveDeleteRequests({ requestIds: selectedRequestIds });

      if (response.success) {
        setSuccess(`Successfully approved ${selectedRequestIds.length} delete request(s). Accounts have been permanently deleted.`);
        setShowBulkApproveModal(false);
        setSelectedRequestIds([]);
        await fetchRequests();
      } else {
        setError(response.error || 'Failed to approve delete requests');
      }
    } catch (err) {
      setError('An error occurred while approving the requests');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkRejectConfirm = async (reason: string) => {
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      const response = await bulkRejectDeleteRequests({ requestIds: selectedRequestIds, reason });

      if (response.success) {
        setSuccess(`Successfully rejected ${selectedRequestIds.length} delete request(s). Users have been notified.`);
        setShowBulkRejectModal(false);
        setSelectedRequestIds([]);
        await fetchRequests();
      } else {
        setError(response.error || 'Failed to reject delete requests');
      }
    } catch (err) {
      setError('An error occurred while rejecting the requests');
    } finally {
      setActionLoading(false);
    }
  };

  const selectedRequests = requests.filter(r => selectedRequestIds.includes(r.requestId));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Dashboard
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Trash2 className="h-8 w-8 text-red-600" />
                Account Deletion Requests
              </h1>
              <p className="text-slate-600 mt-2">
                Review and manage user account deletion requests
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{totalRecords}</p>
              <p className="text-sm text-slate-600">Total Requests</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="h-5 w-5 text-red-600">✕</div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="flex-shrink-0 text-red-600 hover:text-red-800"
            >
              ✕
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="h-5 w-5 text-green-600">✓</div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">Success</p>
              <p className="text-sm text-green-700 mt-1">{success}</p>
            </div>
            <button
              onClick={() => setSuccess('')}
              className="flex-shrink-0 text-green-600 hover:text-green-800"
            >
              ✕
            </button>
          </div>
        )}

        <AdminDeleteRequestFilters
          statusFilter={statusFilter}
          typeFilter={typeFilter}
          dateFrom={dateFrom}
          dateTo={dateTo}
          searchQuery={searchQuery}
          onStatusChange={(status) => {
            setStatusFilter(status);
            setCurrentPage(1);
          }}
          onTypeChange={(type) => {
            setTypeFilter(type);
            setCurrentPage(1);
          }}
          onDateFromChange={(date) => {
            setDateFrom(date);
            setCurrentPage(1);
          }}
          onDateToChange={(date) => {
            setDateTo(date);
            setCurrentPage(1);
          }}
          onSearchChange={setSearchQuery}
          onClearFilters={handleClearFilters}
          stats={stats}
        />

        <BulkActionsToolbar
          selectedCount={selectedRequestIds.length}
          onBulkApprove={handleBulkApproveClick}
          onBulkReject={handleBulkRejectClick}
          onClearSelection={() => setSelectedRequestIds([])}
        />

        <AdminDeleteRequestTable
          requests={requests}
          loading={loading}
          onViewDetails={handleViewDetails}
          onApprove={handleApproveClick}
          onReject={handleRejectClick}
          onReactivate={handleReactivateClick}
          selectedRequestIds={selectedRequestIds}
          onSelectionChange={handleSelectionChange}
        />

        {!loading && requests.length > 0 && (
          <div className="mt-6 bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} requests
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        <AdminDeleteRequestDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          loading={false}
          onApprove={() => selectedRequest && handleApproveClick(selectedRequest.requestId)}
          onReject={() => selectedRequest && handleRejectClick(selectedRequest.requestId)}
        />

        <ApproveDeleteRequestModal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setRequestToAction(null);
          }}
          request={requestToAction}
          onConfirm={handleApproveConfirm}
          loading={actionLoading}
        />

        <RejectDeleteRequestModal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setRequestToAction(null);
          }}
          request={requestToAction}
          onConfirm={handleRejectConfirm}
          loading={actionLoading}
        />

        <BulkApproveDeleteRequestModal
          isOpen={showBulkApproveModal}
          onClose={() => setShowBulkApproveModal(false)}
          selectedRequests={selectedRequests}
          onConfirm={handleBulkApproveConfirm}
        />

        <BulkRejectDeleteRequestModal
          isOpen={showBulkRejectModal}
          onClose={() => setShowBulkRejectModal(false)}
          selectedRequests={selectedRequests}
          onConfirm={handleBulkRejectConfirm}
        />

        <ReactivateAccountModal
          isOpen={showReactivateModal}
          onClose={() => {
            setShowReactivateModal(false);
            setRequestToReactivate(null);
            setReactivationSuccess(false);
          }}
          request={requestToReactivate}
          onConfirm={handleReactivateConfirm}
          loading={actionLoading}
          success={reactivationSuccess}
          onSuccessClose={() => {
            setShowReactivateModal(false);
            setRequestToReactivate(null);
            setReactivationSuccess(false);
          }}
        />
      </div>
    </div>
  );
};
